import { createRoute, useNavigate } from "@tanstack/react-router";

import { PakistanMap } from "../../components/brand/pakistan-map.js";
import { currentBrand } from "../../lib/partner-brand.js";
import { portalSignInPath } from "../../lib/portal-tenant-path.js";
import { tenantScopeRoute } from "../tenant-scope.js";

/**
 * Public welcome page — the unauthenticated landing for a partner
 * portal under `/t/$tenantSlug/`.
 *
 * Branches on the resolved partner brand:
 *   - `transition33` → the AtlasPlate (Pakistan map + 33°N rule + the
 *     "Transition33" wordmark + "Enter the Portal" CTA)
 *   - any other brand (default LATITUDE30) → no welcome surface; we
 *     send the user straight to /portal/sign-in. Per Martyn 2026-05-04
 *     Day 14a: "for non-GoP partners a more generic welcome is fine,
 *     but punt until partner #2 lands."
 *
 * Canon: docs/design-canon/gop/design_handoff_transition33_welcome
 * (high-fidelity AtlasPlate spec).
 *
 * Path: `/t/$tenantSlug/` (tenant-scoped root).
 */
export const portalWelcomeRoute = createRoute({
  getParentRoute: () => tenantScopeRoute,
  path: "/",
  component: PortalWelcome,
});

function PortalWelcome() {
  const brand = currentBrand();
  const navigate = useNavigate();
  const { tenantSlug } = portalWelcomeRoute.useParams();
  const signInPath = portalSignInPath(tenantSlug);

  if (brand.kind === "transition33") {
    return (
      <AtlasPlate
        enterPortalHref={signInPath}
        onEnterPortal={() => {
          void navigate({ href: signInPath });
        }}
      />
    );
  }

  // Default (LATITUDE30 and any future kind without a specific welcome):
  // forward straight to the sign-in form. Renders nothing visible —
  // navigation happens on first render. We keep this as a route rather
  // than a server-side redirect so the SPA's bundle handles it without
  // a network round-trip.
  void navigate({ href: signInPath, replace: true });
  return null;
}

interface AtlasPlateProps {
  enterPortalHref: string;
  onEnterPortal: () => void;
  disableEntranceAnimation?: boolean;
}

function AtlasPlate({ enterPortalHref, onEnterPortal, disableEntranceAnimation }: AtlasPlateProps) {
  // Inline styles + Tailwind side-by-side because the AtlasPlate spec
  // calls for very specific px-level values that don't map cleanly to
  // Tailwind's spacing scale (e.g. 84px wordmark, 320px horizon rule).
  // We preserve the spec verbatim to honour the high-fidelity brief.
  const handleEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onEnterPortal();
  };

  return (
    <div
      className="relative grid h-dvh max-h-dvh w-full overflow-hidden lg:grid-cols-[minmax(320px,42%)_1fr]"
      style={{
        backgroundColor: "var(--color-background)",
        color: "var(--color-fg)",
        fontFamily: "var(--font-sans)",
      }}
      data-disable-entrance={disableEntranceAnimation ? "true" : undefined}
    >
      {/* Four corner registration marks, 12 × 12px crosshairs in teal-800
          0.5px stroke. Reinforce the "survey plate" feel. */}
      <RegistrationMarks />

      {/* ─── Left column ─── */}
      <div
        className="relative flex min-h-0 flex-col px-6 py-8 sm:px-10 lg:px-14 lg:py-10"
        style={{ minWidth: 0 }}
      >
        <MetaStrip />

        <div className="mb-3 flex flex-col gap-2 sm:mb-4 sm:gap-3">
          <span
            className="font-semibold uppercase"
            style={{
              color: "var(--color-brand)",
              fontFamily: "var(--font-sans)",
              fontSize: 11,
              letterSpacing: "0.18em",
            }}
          >
            The Government of Pakistan Portal
          </span>
          <h1
            className="m-0 font-bold"
            style={{
              fontFamily: "var(--font-sans)",
              // Prefer viewport-height so short laptop screens stay one plate.
              fontSize: "clamp(36px, 7.5vh, 72px)",
              lineHeight: 0.92,
              letterSpacing: "-0.04em",
              color: "var(--color-fg)",
            }}
            aria-label="Transition33"
          >
            Transition<span style={{ color: "var(--color-accent)" }}>33</span>
          </h1>
        </div>

        <div
          aria-hidden="true"
          style={{
            height: 2,
            width: "min(280px, 100%)",
            backgroundColor: "var(--color-accent)",
            marginTop: 4,
            flexShrink: 0,
          }}
        />

        <p
          className="m-0 min-h-0"
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "clamp(15px, 2.1vh, 18px)",
            lineHeight: 1.45,
            color: "var(--color-fg-muted)",
            marginTop: "clamp(12px, 2.5vh, 24px)",
            maxWidth: 460,
            fontWeight: 400,
          }}
        >
          A suite of Transition Platforms. Pakistan, the first to co-develop and prove a model that
          other countries will want to adopt.
        </p>

        <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-6">
          <a
            href={enterPortalHref}
            onClick={handleEnter}
            className="inline-flex items-center gap-2.5 no-underline focus:outline-none"
            style={{
              backgroundColor: "var(--color-brand)",
              color: "var(--color-background)",
              padding: "12px 20px",
              fontFamily: "var(--font-sans)",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.04em",
            }}
          >
            Enter the Portal
            <span
              style={{
                fontFamily: "var(--font-mono)",
                color: "var(--color-accent)",
              }}
              aria-hidden="true"
            >
              →
            </span>
          </a>

          <div
            className="text-right uppercase"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "var(--color-fg-subtle)",
            }}
          >
            <div>Latitude30 · Government of Pakistan</div>
            <div style={{ marginTop: 4 }}>Islamabad — London</div>
          </div>
        </div>
      </div>

      {/* ─── Right column: cartographic plate ─── */}
      <div
        className="relative hidden min-h-0 lg:block"
        style={{
          borderLeft: "1px solid var(--color-border)",
          backgroundColor: "color-mix(in srgb, var(--color-background) 92%, white 8%)",
        }}
      >
        {/* Graticule — minor 40px + major 200px */}
        <svg
          aria-hidden="true"
          className="absolute inset-0"
          width="100%"
          height="100%"
          preserveAspectRatio="none"
        >
          <defs>
            <pattern id="t33-grid-minor" width={40} height={40} patternUnits="userSpaceOnUse">
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="rgba(0,56,68,0.10)"
                strokeWidth={0.5}
              />
            </pattern>
            <pattern id="t33-grid-major" width={200} height={200} patternUnits="userSpaceOnUse">
              <path
                d="M 200 0 L 0 0 0 200"
                fill="none"
                stroke="rgba(0,56,68,0.22)"
                strokeWidth={0.5}
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#t33-grid-minor)" />
          <rect width="100%" height="100%" fill="url(#t33-grid-major)" />
        </svg>

        {/* Country shape + parallels + Islamabad + cities */}
        <div className="absolute inset-0" style={{ color: "var(--color-brand)" }}>
          <PakistanMap
            fill="currentColor"
            showLat33
            showIslamabad
            islamabadLabel
            showCities
            showOtherParallels
            style={{ width: "100%", height: "100%" }}
          />
        </div>

        {/* Coordinate annotations */}
        <CoordinateAnnotations />
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────

function RegistrationMarks() {
  // Crosshairs at the four corners, 24px from each edge. The 12×12 SVG
  // has the X stroke at y=6 and Y stroke at x=6.
  const positions = [
    { top: 12, left: 12 },
    { top: 12, right: 12 },
    { bottom: 12, left: 12 },
    { bottom: 12, right: 12 },
  ];
  return (
    <>
      {positions.map((pos, i) => (
        <svg
          key={i}
          aria-hidden="true"
          width={12}
          height={12}
          className="pointer-events-none absolute"
          style={pos}
        >
          <line x1={6} y1={0} x2={6} y2={12} stroke="var(--color-brand)" strokeWidth={0.5} />
          <line x1={0} y1={6} x2={12} y2={6} stroke="var(--color-brand)" strokeWidth={0.5} />
        </svg>
      ))}
    </>
  );
}

function MetaStrip() {
  // Three small-caps mono labels with teal sub-labels. PLATE / SCALE / PROJ.
  const items: ReadonlyArray<{ caption: string; label: string }> = [
    { caption: "PLATE 01", label: "WELCOME" },
    { caption: "SCALE", label: "1 : 9 600 000" },
    { caption: "PROJ.", label: "WGS 84" },
  ];
  return (
    <div
      className="mb-6 flex shrink-0 gap-8 uppercase sm:mb-8 sm:gap-10 lg:mb-10"
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.14em",
        color: "var(--color-fg-subtle)",
      }}
    >
      {items.map((item) => (
        <div key={item.caption}>
          <div style={{ color: "var(--color-brand)", marginBottom: 4 }}>{item.caption}</div>
          <div>{item.label}</div>
        </div>
      ))}
    </div>
  );
}

function CoordinateAnnotations() {
  // Top-right + bottom-right blocks. Mono caps small text.
  const tagStyle: React.CSSProperties = {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    color: "var(--color-fg-subtle)",
  };
  return (
    <>
      <div className="absolute right-8 top-8 text-right">
        <div style={{ ...tagStyle, color: "var(--color-accent-hover)" }}>33°43′N · 73°05′E</div>
        <div style={{ ...tagStyle, marginTop: 4 }}>Federal Capital Territory</div>
      </div>
      <div className="absolute bottom-8 right-8 text-right">
        <div style={tagStyle}>Islamic Republic of Pakistan</div>
        <div style={{ ...tagStyle, marginTop: 4 }}>881 913 km² · pop. 241M</div>
      </div>
    </>
  );
}
